// Central client-side error reporting hook.
//
// Today it logs to the console and calls an optional `window.__reportError` sink. This is
// the single wire-up point for a RUM / error-tracking service (e.g. Datadog Browser RUM
// `datadogRum.addError(...)` or Sentry `captureException(...)`) so that self-deploying
// builds get a real-time client-error signal to health-gate / auto-rollback on. Kept
// dependency-free until a sink is chosen; wiring one is a one-line change here.
export interface ErrorContext {
    componentStack?: string | null;
    route?: string;
    [key: string]: unknown;
}

// Redact secret-bearing values (OAuth `code`, tokens, etc.) from a route string before it is
// logged or forwarded to a sink. Keeps the path and param keys for diagnostics; masks values.
const SENSITIVE_PARAM = /^(code|access_token|refresh_token|id_token|token|state|client_secret|secret|password|api_key)$/i;
function redactRoute(route?: string): string | undefined {
    if (!route) return route;
    return route.replace(/([?&#])([^=&#]+)=([^&#]*)/g, (match, sep, key) =>
        SENSITIVE_PARAM.test(decodeURIComponent(key)) ? `${sep}${key}=<redacted>` : match);
}

export function reportError(error: unknown, context: ErrorContext = {}): void {
    try {
        const safeContext: ErrorContext = { ...context, route: redactRoute(context.route) };
        // eslint-disable-next-line no-console
        console.error('[reportError]', error, safeContext);
        const sink = (window as unknown as { __reportError?: (e: unknown, c: ErrorContext) => void }).__reportError;
        if (typeof sink === 'function') sink(error, safeContext);
        // RUM wire-up point (enable once a sink is configured):
        // (window as any).DD_RUM?.addError?.(error, safeContext);
    } catch {
        // Error reporting must never itself throw.
    }
}
